import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { SxProps } from "@mui/material/styles";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import { useTranslation } from "react-i18next";
import { useUpdateNumberOfTables } from "src/data/restaurant/queries/restaurant";
import { CustomButton, TextInput } from "src/design";
import {
  colorBorder,
  colorSurface,
  colorTextMuted,
  colorTextPrimary,
  colorTextError,
  iconBgSuccess,
  iconColorSuccess,
  radiusCard,
  radiusInner,
  shadowCard,
} from "src/design/tokens";

interface TableSetupPromptProps {
  restaurantId: string;
  canUpdate: boolean;
  onSuccess: () => void;
  sx?: SxProps;
}

export const TableSetupPrompt = ({
  restaurantId,
  canUpdate,
  onSuccess,
  sx,
}: TableSetupPromptProps) => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");

  const { mutate, isPending, isError } = useUpdateNumberOfTables(
    restaurantId,
    onSuccess,
  );

  const parsed = Number(value);
  const isValid = value.trim() !== "" && Number.isInteger(parsed) && parsed > 0;

  const handleSubmit = () => {
    if (!isValid) return;
    mutate(parsed);
  };

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3, md: 4 },
        py: 6,
        width: "100%",
        display: "flex",
        justifyContent: "center",
        ...sx,
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 460,
          backgroundColor: colorSurface,
          border: `1px solid ${colorBorder}`,
          borderRadius: radiusCard,
          boxShadow: shadowCard,
          p: { xs: 3, md: 4 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: radiusInner,
            backgroundColor: iconBgSuccess,
            color: iconColorSuccess,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <TableRestaurantIcon sx={{ fontSize: 28 }} />
        </Box>

        <Typography sx={{ fontSize: 20, fontWeight: 700, color: colorTextPrimary }}>
          {t("tableSchedule.setup.title")}
        </Typography>
        <Typography sx={{ fontSize: 14, color: colorTextMuted, lineHeight: 1.5 }}>
          {t("tableSchedule.setup.description")}
        </Typography>

        <Box sx={{ width: "100%", mt: 1 }}>
          <TextInput
            label={t("tableSchedule.setup.inputLabel")}
            value={value}
            onChange={setValue}
            type="number"
            fullwidth
            disabled={!canUpdate || isPending}
            error={value.trim() !== "" && !isValid}
          />
        </Box>

        {isError && (
          <Typography sx={{ fontSize: 13, color: colorTextError }}>
            {t("tableSchedule.setup.error")}
          </Typography>
        )}

        {!canUpdate ? (
          <Typography sx={{ fontSize: 13, color: colorTextMuted }}>
            {t("tableSchedule.setup.noPermission")}
          </Typography>
        ) : (
          <CustomButton
            color="black"
            variant="contained"
            onClick={handleSubmit}
            disabled={!isValid || isPending}
            sx={{ width: "100%" }}
          >
            {t("tableSchedule.setup.submit")}
          </CustomButton>
        )}
      </Box>
    </Box>
  );
};
